import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { cn } from '../../../lib/utils.js';
import {
  ShellIconBuilding,
  ShellIconCheck,
  ShellIconChevronDown,
  ShellIconLayers,
  ShellIconLock,
} from '../app-shell/shell-stroke-icon.js';
import { ContextPill, ContextPillStatic } from '../context-pill/context-pill.js';

export type ContextSwitcherLevel = 'place' | 'tenant' | 'network' | 'platform';

export interface ContextSwitcherItem {
  id: string;
  label: string;
  subtitle?: string;
}

export interface ContextSwitcherSection {
  level: ContextSwitcherLevel;
  label: string;
  items: ContextSwitcherItem[];
  activeId: string | null;
}

export interface ContextSwitcherProps {
  sections: ContextSwitcherSection[];
  contextLabel: string;
  isStatic?: boolean;
  levelIcon?: ReactNode;
  onSelect: (level: ContextSwitcherLevel, entityId: string) => void;
  className?: string;
}

const LEVEL_ICONS: Record<ContextSwitcherLevel, typeof ShellIconBuilding> = {
  place: ShellIconBuilding,
  tenant: ShellIconBuilding,
  network: ShellIconLayers,
  platform: ShellIconLock,
};

function resolveLevelIcon(level: ContextSwitcherLevel): ReactNode {
  const Icon = LEVEL_ICONS[level];
  return <Icon size={13} className="t-muted" />;
}

export function ContextSwitcher({
  sections,
  contextLabel,
  isStatic = false,
  levelIcon,
  onSelect,
  className,
}: ContextSwitcherProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const activeLevel = sections.find((section) =>
    section.items.some((item) => item.id === section.activeId),
  )?.level;

  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const icon =
    levelIcon ??
    (activeLevel ? (
      resolveLevelIcon(activeLevel)
    ) : (
      <ShellIconBuilding size={13} className="t-muted" />
    ));

  if (isStatic || sections.length === 0) {
    return (
      <ContextPillStatic
        className={className}
        label={contextLabel}
        icon={icon}
        aria-label="Contexto de navegação"
      />
    );
  }

  return (
    <div ref={rootRef} className={cn('ctx-switcher', className)}>
      <ContextPill
        type="button"
        aria-label="Trocar contexto de navegação"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        label={contextLabel}
        icon={icon}
        trailingIcon={<ShellIconChevronDown size={12} className="t-subtle" />}
        onClick={() => {
          setOpen((current) => !current);
        }}
      />

      {open ? (
        <div id={menuId} className="ctx-switcher-menu" role="menu">
          {sections.map((section, sectionIndex) => (
            <div key={section.level}>
              {sectionIndex > 0 ? <div className="ctx-switcher-divider" role="separator" /> : null}
              <div className="ctx-switcher-label">{section.label}</div>
              {section.items.map((item) => {
                const isActive = section.activeId === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="menuitem"
                    className="ctx-switcher-item"
                    onClick={() => {
                      setOpen(false);
                      onSelect(section.level, item.id);
                    }}
                  >
                    {resolveLevelIcon(section.level)}
                    <span className="ctx-switcher-item-text">
                      <span className="ctx-switcher-item-title">{item.label}</span>
                      {item.subtitle ? (
                        <span className="ctx-switcher-item-subtitle t-xs t-subtle">
                          {item.subtitle}
                        </span>
                      ) : null}
                    </span>
                    {isActive ? (
                      <ShellIconCheck size={13} className="ctx-switcher-check c-success" />
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
